import { initAxios } from '../utils/axiosHelper';

/**
 * Export Hub client API. Uses initAxios() so the Keycloak bearer token is
 * attached on every request (the server route is behind keycloak.protect()).
 * The server returns the bite_db-style `{ success, data }` envelope, so each
 * method unwraps `response.data.data`. Grows method-by-method alongside the backend.
 */

// Returns a fresh keycloak-aware axios instance. Called per-request so the
// interceptor always has access to the current token.
const getClient = () => initAxios();

export type ExportVisibility = 'Private' | 'Shared';
export type ExportDataSource = 'manual' | 'file' | 'integration';

export interface ExportDatabase {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  accent_color: string | null;
  visibility: ExportVisibility;
  data_source: ExportDataSource;
  group_id: string;
  created_by: string;
  created_by_name: string | null;
  is_archived: boolean;
  created_at: string;
  last_updated: string;
  last_activity_at: string;
  // Derived (zero until later slices populate products/screenings).
  product_count: number;
  compliance_health: number;
  recent_screening_count: number;
}

export interface CreateExportDatabaseInput {
  name: string;
  description?: string;
  icon?: string;
  accent_color?: string;
  visibility?: ExportVisibility;
  data_source?: ExportDataSource;
  group_id: string;
}

export type UpdateExportDatabaseInput = Partial<{
  name: string;
  description: string | null;
  icon: string | null;
  accent_color: string | null;
  visibility: ExportVisibility;
}>;

// ---- Products --------------------------------------------------------------

export type ExportProductTier = 'Component' | 'Sub-assembly' | 'Assembly';
export type ExportJurisdiction =
  | 'ITAR'
  | 'EAR'
  | 'NRC'
  | 'EAR99'
  // No regime determined yet. Distinct from EAR99, which is itself a
  // conclusion ("subject to the EAR, not on the CCL") — a product nobody has
  // classified must not claim it.
  | 'Unknown'
  | 'Not Subject';
export type ExportComplianceStatus =
  | 'Compliant'
  | 'In Review'
  | 'Action Required'
  | 'Restricted'
  | 'Unscreened'
  | 'Needs Input';

// One input a product still needs before results can be generated (screening
// plan Step 2). Computed server-side and returned on product detail reads.
export interface MissingInput {
  key: string;
  label: string;
  description: string;
  severity: 'blocking' | 'advisory';
  affects: string;
  action: 'set_field' | 'set_jurisdiction' | 'classify';
}

export interface ExportProduct {
  id: string;
  name: string;
  internal_sku: string | null;
  description: string | null;
  manufacturer: string | null;
  country_of_origin: string | null;
  materials: string | null;
  intended_end_use: string | null;
  hardware_authorized: boolean;
  tech_data_authorized: boolean;
  quantity: number | null;
  unit_of_measure: string | null;
  hts_code: string | null;
  eccn: string | null;
  // A reviewer's manual "License Required" answer, overriding the Country
  // Screening derivation. null = no manual answer, use screening.
  license_required_override: boolean | null;
  intended_destination_countries: string[];
  tier: ExportProductTier;
  jurisdiction: ExportJurisdiction;
  jurisdiction_details: Record<string, unknown>;
  compliance_status: ExportComplianceStatus;
  last_screened_at: string | null;
  group_id: string;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  last_updated: string;
  database_id: string | null;
  active_license_count: number;
  // Populated only for products listed in a license's "Linked Products": the
  // planning ceilings held on that link. These are budgets — what the product
  // actually drew down comes from the drawdown ledger.
  allocated_value?: number;
  allocated_quantity?: number | null;
  default_unit_value?: number | null;
  // This license line's own classification (DSP-5 Block 11), which can differ
  // from the product's day-to-day recorded USML category.
  line_usml_category?: string | null;
  commodity_code?: string | null;
  // Readiness issues (screening plan Step 2) — present on product detail reads.
  missing_inputs?: MissingInput[];
}

// ---- Bulk classification runs (screening plan Step 4) ----

export type ExportScreeningRunStatus =
  | 'processing'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export interface ExportScreeningRun {
  id: string;
  group_id: string;
  database_id: string;
  kind: 'classification' | 'denied_party' | 'full' | 'product_risk';
  status: ExportScreeningRunStatus;
  chunks_total: number;
  chunks_processed: number;
  products_total: number;
  products_processed: number;
  products_flagged: number;
  error_summary: Array<{ message: string; count?: number }>;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  last_updated: string;
}

export type ExportClassificationRegime =
  | 'schedule_b'
  | 'country'
  | 'export_screening'
  | 'nrc'
  | 'itar'
  | 'ear_ccl';

export interface ExportClassificationResult {
  id: string;
  product_id: string;
  group_id: string;
  run_id: string | null;
  regime: ExportClassificationRegime;
  status:
    | 'cleared'
    | 'license_required'
    | 'flagged'
    | 'needs_review'
    | 'no_data'
    | 'error';
  result: Record<string, unknown>;
  source: 'derived' | 'ai';
  eccn: string | null;
  created_at: string;
}

// Ingest a completed Export Workflow's output into the Hub (WS1).
export interface IngestWorkflowInput {
  group_id: string;
  database_id: string;
  product_id?: string;
  internal_sku?: string;
  name?: string;
  source_case_id?: string;
  /** Ticket name of the saved workflow run, shown as provenance on the product. */
  source_case_name?: string;
  workflow_state: Record<string, unknown>;
}

export interface IngestWorkflowResult {
  product_id: string;
  created: boolean;
  regimes_written: ExportClassificationRegime[];
  compliance_status: string;
}

export interface LicenseDocument {
  id: string;
  license_id: string;
  group_id: string;
  file_name: string;
  content_type: string | null;
  file_size: number | null;
  uploaded_by_name: string | null;
  created_at: string;
}

export interface ProductDocument {
  id: string;
  product_id: string;
  group_id: string;
  file_name: string;
  content_type: string | null;
  file_size: number | null;
  uploaded_by_name: string | null;
  created_at: string;
}

export interface ProductComment {
  id: string;
  product_id: string;
  group_id: string;
  body: string;
  author_id: string | null;
  author_name: string | null;
  created_at: string;
}

export interface CreateExportProductInput {
  name: string;
  internal_sku?: string;
  description?: string;
  manufacturer?: string;
  country_of_origin?: string;
  materials?: string;
  intended_end_use?: string;
  hardware_authorized?: boolean;
  tech_data_authorized?: boolean;
  quantity?: number;
  unit_of_measure?: string;
  hts_code?: string;
  eccn?: string;
  // Explicit null clears the manual answer back to the screening derivation.
  license_required_override?: boolean | null;
  intended_destination_countries?: string[];
  tier?: ExportProductTier;
  jurisdiction?: ExportJurisdiction;
  // Wholesale replace — prefer the patch below when the caller read the
  // product first, or a concurrent write to the blob is lost.
  jurisdiction_details?: Record<string, unknown>;
  // Merge/remove individual keys against the live row: string sets, null
  // removes, unmentioned keys are left alone. Mutually exclusive with the above.
  jurisdiction_details_patch?: Record<string, string | null>;
  compliance_status?: ExportComplianceStatus;
  group_id: string;
}

// ---- Bulk product import (screening plan Step 1) ----

export type ExportImportStatus =
  | 'processing'
  | 'completed'
  | 'completed_with_errors'
  | 'failed';

export interface ExportImportRowError {
  row: number;
  issues: string[];
}

export interface ExportImportJob {
  id: string;
  group_id: string;
  database_id: string;
  file_name: string | null;
  status: ExportImportStatus;
  rows_total: number;
  rows_inserted: number;
  rows_failed: number;
  errors: ExportImportRowError[];
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  last_updated: string;
}

export type UpdateExportProductInput = Partial<
  Omit<CreateExportProductInput, 'group_id'>
>;

export interface ProductsPage {
  products: ExportProduct[];
  pagination: {
    currentPage: number;
    totalItems: number;
    itemsPerPage: number;
    totalPages: number;
  };
}

export interface ListProductsParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  // Facet filters (comma-separated values per facet).
  status?: string;
  jurisdiction?: string;
  tier?: string;
  origin?: string;
  dest?: string;
  hts?: string;
  needs_jurisdiction?: string;
  database?: string;
}

export interface ProductFacetOptions {
  origins: string[];
  destinations: string[];
  htsChapters: string[];
  total: number;
}

// ---- Saved Views -----------------------------------------------------------

export interface SavedView {
  id: string;
  group_id: string;
  scope: string;
  name: string;
  facets: Record<string, unknown>;
  is_pinned: boolean;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  last_updated: string;
}

export interface CreateSavedViewInput {
  scope: string;
  name: string;
  facets?: Record<string, unknown>;
  is_pinned?: boolean;
  group_id: string;
}

export type UpdateSavedViewInput = Partial<{
  name: string;
  facets: Record<string, unknown>;
  is_pinned: boolean;
}>;

// ---- Screenings ------------------------------------------------------------

// Every type a stored screening can carry. Only export_screening can still be
// run; the others are denied-party screens of linked entities from before that
// was removed from the Export Hub.
export type ExportScreeningType =
  | 'schedule_b'
  | 'country_screening'
  | 'screening_compliance'
  // Product-level export risk screen: commodity screen + opt-in AI threat eval.
  | 'export_screening';

// One commodity-index hit from the product export risk screen (result_data of
// an `export_screening` result).
export interface CommodityScreenRecord {
  hs_code: string;
  bite_lens: string;
  risk_country: string;
  list: string;
  trade_type: string;
  score?: number;
  source?: Record<string, any>;
}

export interface ScreeningAiEvaluation {
  hts_code: string;
  trade_type: string;
  risk: string;
  burden: string;
  key_factors: string[];
  summary: string;
  date: string;
}

export interface ScreeningLensRecord {
  entity: string;
  country: string;
  agency: string;
  list: string;
  date_added: string;
  // The linked party (customer/consignee/end-user) this hit was screened under,
  // so a screen covering several entities shows which one each hit belongs to.
  matched_for?: string;
  score?: number;
  // Full OpenSearch `_source` document for the hit — powers the record drawer.
  source?: Record<string, any>;
}

export interface ScreeningLens {
  lens: string;
  total: number;
  new: number;
  records: ScreeningLensRecord[];
}

export interface ScreeningResult {
  id: string;
  product_id: string;
  group_id: string;
  check_type: ExportScreeningType;
  status: 'in_progress' | 'completed' | 'failed';
  risk: string | null;
  summary: string | null;
  ai_evaluation: ScreeningAiEvaluation | Record<string, never>;
  result_data:
    | {
        lenses?: ScreeningLens[];
        records?: CommodityScreenRecord[];
        // Set when a screen came back at the datahub fetch cap, so the panel
        // can say the set is incomplete rather than implying it's everything.
        truncated?: boolean;
      }
    | Record<string, never>;
  run_options: Record<string, unknown>;
  version_date: string;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  last_updated: string;
}

// ---- Database screening settings -------------------------------------------

export type ExportScreeningFrequency = '' | 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';
export type ExportNotificationMethod = 'in_app' | 'email' | 'both' | 'none';

// The commodity lenses a database can screen against (mirrors the server's
// EXPORT_COMMODITY_LENSES + automations DEFAULT_COMMODITY_LENSES).
export const EXPORT_COMMODITY_LENSES = [
  'AD CVD Commodities',
  'AD CVD Entities',
  'Consumer Protection',
  'ESG Commodities',
  'Forced Labor Commodities',
  'Nomenclatures - HTS Codes Schedule B and CN Codes',
  'PGA Codes',
  'Sensitive Technology',
  'US Duties & Tariffs',
  'US Monitoring and Regulations',
] as const;

export interface ExportDatabaseSettings {
  screening_frequency: string | null;
  screening_day_of_week: string | null;
  last_screen_timestamp: string | null;
  notification_method: ExportNotificationMethod;
  notifications_email: string | null;
  screening_lenses: string[] | null;
}

export interface UpdateDatabaseSettingsInput {
  group_id: string;
  screening_frequency?: ExportScreeningFrequency;
  screening_day_of_week?: string | null;
  notification_method?: ExportNotificationMethod;
  notifications_email?: string | null;
  screening_lenses?: string[] | null;
}

export interface RunScreeningInput {
  check_type: 'export_screening';
  group_id: string;
  options?: {
    // Opt in to the AI threat evaluation.
    generate_risk?: boolean;
  };
}

// ---- Licenses + Checklists -------------------------------------------------

export type ExportLicenseType =
  | 'DSP-5'
  | 'DSP-73'
  | 'DSP-61'
  | 'DSP-85'
  | 'TAA'
  | 'MLA'
  | 'WDA'
  | 'Other';
export type ExportAgency = 'State / DDTC' | 'Commerce / BIS' | 'Other';
export type ExportLicenseStatus =
  | 'Requested'
  | 'Active'
  | 'Expiring'
  | 'Expired'
  | 'Denied'
  | 'Closed';

export interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
}

// Which roll-up a license's authorized value is managed against. Drawdowns
// record both product and ECCN either way, so this only selects the view.
export const LICENSE_VALUE_BASES = ['product', 'eccn'] as const;
export type ExportLicenseValueBasis = (typeof LICENSE_VALUE_BASES)[number];

export interface ExportLicense {
  id: string;
  group_id: string;
  database_id: string | null;
  license_type: ExportLicenseType;
  agency: ExportAgency;
  status: ExportLicenseStatus;
  license_number: string | null;
  business_unit: string | null;
  business_segment: string | null;
  eccns: string[];
  /** 'product' (default) tracks value per linked product; 'eccn' per ECCN. */
  value_basis: ExportLicenseValueBasis;
  /**
   * The license's default USML category (DSP-5 Block 11), inherited by new
   * line items. Null for EAR licenses.
   */
  usml_category: string | null;
  /**
   * Distinct USML categories across this license's line items. Present on list
   * reads; a license may span several, so this is what the lines actually say.
   */
  line_usml_categories?: string[];
  proposed_value: number | null;
  value: number | null;
  value_used: number;
  effective_date: string | null;
  expiry_date: string | null;
  date_submitted: string;
  request_details: Record<string, unknown>;
  approval_details: Record<string, unknown>;
  checklist: ChecklistItem[];
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  last_updated: string;
  linked_product_count: number;
  // Present only on a product's linked-license read: the planning ceilings held
  // on the join row for that one product. Consumption lives in the ledger.
  allocated_value?: number;
  allocated_quantity?: number | null;
  default_unit_value?: number | null;
}

export interface CreateLicenseInput {
  group_id: string;
  database_id?: string;
  product_id?: string;
  license_type: ExportLicenseType;
  agency?: ExportAgency;
  business_unit?: string;
  business_segment?: string;
  internal_transaction_number?: string;
  signatory?: string;
  license_custodian?: string;
  country?: string;
  country_of_ultimate_destination?: string;
  customer?: string;
  foreign_end_user?: string;
  cw_role?: string;
  agreement_owner?: string;
  cw_parties?: string[];
  sublicensees?: string[];
  foreign_parties?: string[];
  program_name?: string;
  program_description?: string;
  quantity?: number;
  unit_of_measure?: string;
  hardware_authorized?: boolean;
  tech_data_authorized?: boolean;
  proposed_value?: number;
  eccns?: string[];
  /** USML category for an ITAR license (DSP-5 Block 11). */
  usml_category?: string;
  comments?: string;
  checklist?: Array<{ id?: string; label: string; done?: boolean }>;
}

export type UpdateLicenseInput = Partial<{
  status: ExportLicenseStatus;
  agency: ExportAgency;
  license_number: string | null;
  business_unit: string | null;
  business_segment: string | null;
  eccns: string[];
  value_basis: ExportLicenseValueBasis;
  usml_category: string | null;
  proposed_value: number | null;
  value: number | null;
  effective_date: string | null;
  expiry_date: string | null;
  checklist: Array<{ id?: string; label: string; done?: boolean }>;
  request_details: Record<string, unknown>;
  approval_details: Record<string, unknown>;
}>;

export interface ListLicensesParams {
  search?: string;
  type?: string;
  agency?: string;
  status?: string;
}

// ---- License drawdown (per-transaction value) ------------------------------

export type ExportDrawdownType = 'Shipment' | 'Return' | 'Adjustment';
export type ExportDrawdownStatus = 'Draft' | 'Posted' | 'Void';

export interface LicenseDrawdown {
  id: string;
  group_id: string;
  license_id: string;
  product_id: string | null;
  product_name: string | null;
  eccn: string | null;
  drawdown_type: ExportDrawdownType;
  status: ExportDrawdownStatus;
  reference: string | null;
  transaction_date: string;
  // Signed: Return rows carry a negative quantity and value so the ledger sums.
  quantity: number | null;
  unit_of_measure: string | null;
  unit_value: number | null;
  value: number;
  // True when the total was typed rather than derived from qty × unit value.
  value_overridden: boolean;
  currency: string;
  destination_country: string | null;
  consignee: string | null;
  overdraw_reason: string | null;
  notes: string | null;
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  last_updated: string;
  // Populated on product-side reads, which span licenses.
  license_number: string | null;
  license_type: ExportLicenseType | null;
}

export interface CreateDrawdownInput {
  group_id: string;
  product_id?: string;
  eccn?: string;
  drawdown_type?: ExportDrawdownType;
  status?: ExportDrawdownStatus;
  reference?: string;
  transaction_date?: string;
  quantity?: number;
  unit_of_measure?: string;
  unit_value?: number;
  // Omit to take qty × unit value; send it to override the calculation.
  value?: number;
  currency?: string;
  destination_country?: string;
  consignee?: string;
  overdraw_reason?: string;
  notes?: string;
}

export type UpdateDrawdownInput = Partial<Omit<CreateDrawdownInput, 'group_id'>>;

export interface DrawdownEccnRollup {
  eccn: string;
  applied: number;
  quantity: number;
}

export interface DrawdownProductRollup {
  product_id: string;
  product_name: string;
  drawn_value: number;
  drawn_quantity: number;
  allocated_value: number;
  allocated_quantity: number | null;
  default_unit_value: number | null;
  transaction_count: number;
}

export interface ImportShipmentsResult {
  rows_total: number;
  imported: number;
  failed: number;
  licenses_touched: number;
  errors: Array<{ row: number; issues: string[] }>;
}

export interface DrawdownSummary {
  license_id: string;
  authorized_value: number | null;
  posted_value: number;
  draft_value: number;
  value_remaining: number | null;
  authorized_quantity: number | null;
  posted_quantity: number;
  quantity_remaining: number | null;
  posted_count: number;
  draft_count: number;
  by_eccn: DrawdownEccnRollup[];
  by_product: DrawdownProductRollup[];
}

export interface ChecklistTemplate {
  id: string;
  group_id: string;
  name: string;
  items: ChecklistItem[];
  created_by: string;
  created_by_name: string | null;
  created_at: string;
  last_updated: string;
}

export interface CreateChecklistTemplateInput {
  group_id: string;
  name: string;
  items?: Array<{ id?: string; label: string; done?: boolean }>;
}

export type UpdateChecklistTemplateInput = Partial<{
  name: string;
  items: Array<{ id?: string; label: string; done?: boolean }>;
}>;

// ---- Linking + bulk + activity ---------------------------------------------

export interface ExportActivity {
  id: string;
  group_id: string;
  subject_type: 'product' | 'license';
  subject_id: string;
  action: string;
  message: string;
  actor_id: string | null;
  actor_name: string | null;
  created_at: string;
}

export interface LinkLicensesInput {
  group_id: string;
  license_ids: string[];
  notes?: string;
  cascade?: 'self' | 'children' | 'descendants';
}

// ---- Hierarchy (BOM) ----
export interface ProductStructureNode {
  id: string;
  name: string;
  internal_sku: string | null;
  tier: ExportProductTier;
  compliance_status: ExportComplianceStatus;
  quantity: number | null;
}

export interface ProductStructure {
  children: ProductStructureNode[];
  parents: ProductStructureNode[];
  child_issue_count: number;
}

export interface ProductBomEdge {
  parent_product_id: string;
  child_product_id: string;
  quantity: number | null;
}

export interface AddBomLinkInput {
  group_id: string;
  parent_product_id: string;
  child_product_id: string;
  quantity?: number;
  unit_of_measure?: string;
  notes?: string;
}

// ---- Entities (spec §4.7) ----

export type ExportEntityType =
  | 'Customer'
  | 'Consignee'
  | 'End User'
  | 'Distributor'
  | 'Manufacturer';
export type ProductEntityRole = ExportEntityType;

export interface ExportEntity {
  id: string;
  name: string;
  entity_type: ExportEntityType;
  country: string | null;
  address: string | null;
  registration_id: string | null;
  risk_flags: string[];
  source_ref: string | null;
  group_id: string;
  created_at: string;
  last_updated: string;
}

export interface ProductEntityLink extends ExportEntity {
  role: ProductEntityRole;
  link_notes: string | null;
  linked_at: string;
}

export interface LinkEntityInput {
  group_id: string;
  entity_id: string;
  role: ProductEntityRole;
  notes?: string;
}

export interface CreateEntityInput {
  group_id: string;
  name: string;
  entity_type: ExportEntityType;
  country?: string;
  address?: string;
  registration_id?: string;
}

// Fields whose creatable-select options persist server-side per team.
export type FieldOptionField =
  | 'business_unit'
  | 'business_segment'
  | 'cw_role'
  | 'country'
  | 'custodian'
  | 'signatory';

// ---- Dashboards + Level 1 overviews (spec §5.2/§5.3/§9) ----

export interface CountryStat {
  country: string;
  count: number;
  value_remaining?: number;
}
export interface RiskBucket {
  risk: string;
  count: number;
}
export interface DashboardIssueItem {
  id: string;
  name: string;
  compliance_status: ExportComplianceStatus;
  tier: ExportProductTier;
}
export interface LicenseTypeStat {
  license_type: string;
  count: number;
  value_remaining: number;
}
// One license's consumption against its authorized value (dashboard "License
// usage"). `value_remaining` / `pct_used` are null when the license has no
// recorded ceiling — rendered as "no value ceiling", not 0%.
export interface LicenseUsageRow {
  id: string;
  license_number: string | null;
  license_type: string;
  status: string;
  value: number | null;
  value_used: number;
  value_remaining: number | null;
  /** Fraction of the ceiling consumed (0–1; >1 when over-drawn). */
  pct_used: number | null;
  linked_product_count: number;
  expiry_date: string | null;
  days_left: number | null;
}

// Licenses at or above this share of their ceiling are "nearly exhausted".
// Matches the server's LICENSE_UTILIZATION_THRESHOLD.
export const LICENSE_UTILIZATION_THRESHOLD = 0.9;

export type HighRiskDriverKey =
  | 'itar'
  | 'nrc'
  | 'restricted'
  | 'screen_high'
  | 'uncovered_destination';

export interface HighRiskDriver {
  key: HighRiskDriverKey;
  count: number;
}
export interface HighRiskProduct {
  id: string;
  name: string;
  internal_sku: string | null;
  jurisdiction: ExportJurisdiction;
  compliance_status: ExportComplianceStatus;
  risk: string | null;
  drivers: HighRiskDriverKey[];
}
export interface HighRiskSummary {
  /** Distinct products tripping at least one driver — not the sum of drivers. */
  total: number;
  drivers: HighRiskDriver[];
  products: HighRiskProduct[];
}

export interface ExpiringLicense {
  id: string;
  license_number: string | null;
  license_type: string;
  expiry_date: string | null;
  value_remaining: number | null;
  days_left: number;
}

export interface DatabaseDashboard {
  core: {
    total: number;
    compliant: number;
    in_review: number;
    action_required: number;
    restricted: number;
    unscreened: number;
    needs_input: number;
    active_licenses: number;
    value_remaining: number;
    recent_screenings_7d: number;
    total_screenings: number;
  };
  hierarchy: {
    assemblies: number;
    orphaned_components: number;
    assemblies_with_child_issues: number;
  };
  coverage: {
    unlinked_products: number;
    destinations_without_coverage: number;
    needs_jurisdiction: number;
  };
  risk_distribution: RiskBucket[];
  open_issues: DashboardIssueItem[];
  license_usage: LicenseUsageRow[];
  high_risk: HighRiskSummary;
  license_by_country: CountryStat[];
  product_by_destination: CountryStat[];
  license_by_type: LicenseTypeStat[];
  expiring_licenses: ExpiringLicense[];
}

// ---- Database history (header History icon) --------------------------------

export type DatabaseHistoryKind = 'activity' | 'screening' | 'run' | 'import';

export interface DatabaseHistoryEntry {
  id: string;
  kind: DatabaseHistoryKind;
  created_at: string;
  action: string;
  message: string;
  actor_name: string | null;
  subject_type: 'product' | 'license' | 'database';
  /** The product/license the entry is about; null for database-level entries. */
  subject_id: string | null;
  subject_name: string | null;
  risk: string | null;
}

export interface DatabaseHistoryPage {
  entries: DatabaseHistoryEntry[];
  total: number;
}

// One saved screening result, database-scoped (report export).
export interface DatabaseScreeningResult {
  id: string;
  product_id: string;
  product_name: string;
  internal_sku: string | null;
  check_type: string;
  status: string;
  risk: string | null;
  summary: string | null;
  created_at: string;
  created_by_name: string | null;
}

export interface OverviewProduct extends ExportProduct {
  database_name: string | null;
}
export interface OverviewProductsPage {
  products: OverviewProduct[];
  pagination: ProductsPage['pagination'];
}
export interface OverviewFacetOptions extends ProductFacetOptions {
  databases: Array<{ id: string; name: string }>;
}
export interface OverviewKpis {
  total: number;
  compliant: number;
  action_required: number;
  restricted: number;
  unscreened: number;
  recent_screenings_7d: number;
}

export interface EccnCoverageStat {
  eccn: string;
  license_count: number;
  country_count: number;
  value: number;
}
export interface LicenseOverview {
  kpis: {
    active_licenses: number;
    active_countries: number;
    authorized_value: number;
    value_remaining: number;
    expiring_30: number;
    expiring_60: number;
    expiring_90: number;
    // Drawdown rollups: consumed_value is the ledger's posted total across
    // active licenses; the counts drive the exhaustion tile.
    consumed_value: number;
    nearly_exhausted: number;
    over_drawn: number;
  };
  by_country: CountryStat[];
  by_type: LicenseTypeStat[];
  by_agency: Array<{ agency: string; count: number }>;
  eccn_coverage: EccnCoverageStat[];
  expiring_licenses: ExpiringLicense[];
}

// ---- Notifications feed (spec §5.4) ----

export interface WorkspaceFeedItem {
  id: string;
  kind: string;
  message: string;
  title: string | null;
  subject_type: 'product' | 'license' | null;
  product_id: string | null;
  database_id: string | null;
  actor_name: string | null;
  created_at: string;
  is_issue: boolean;
}

export interface LinkProductsInput {
  group_id: string;
  product_ids: string[];
  notes?: string;
  /**
   * Applied to every line this link creates, overriding each product's own
   * recorded category. Omit to let each product bring its own.
   */
  usml_category?: string;
}

export interface BulkScope {
  ids?: string[];
  search?: string;
  filters?: {
    status?: string[];
    jurisdiction?: string[];
    tier?: string[];
    origin?: string[];
    destination?: string[];
    htsChapter?: string[];
    needsJurisdiction?: boolean;
  };
}

export type BulkAction =
  | 'set_jurisdiction'
  | 'set_country_of_origin'
  | 'set_compliance_status'
  | 'link_license'
  | 'restore_field';

export interface BulkApplyInput {
  group_id: string;
  scope: BulkScope;
  action: BulkAction;
  params: Record<string, unknown>;
}

export interface BulkApplyResult {
  affected: number;
  field?: string;
  previous?: Array<{ id: string; value: unknown }>;
  linked_license_id?: string;
}

const BASE = '/export-hub';

export class ExportHubAPI {
  static async getDatabases(groupId: string): Promise<ExportDatabase[]> {
    const res = await getClient().get(`${BASE}/databases`, { params: { groupId: groupId } });
    return res.data?.data ?? [];
  }

  static async getDatabase(
    id: string,
    groupId: string,
  ): Promise<ExportDatabase> {
    const res = await getClient().get(`${BASE}/databases/${id}`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async createDatabase(
    input: CreateExportDatabaseInput,
  ): Promise<ExportDatabase> {
    const res = await getClient().post(`${BASE}/databases`, input);
    return res.data?.data;
  }

  static async updateDatabase(
    id: string,
    groupId: string,
    input: UpdateExportDatabaseInput,
  ): Promise<ExportDatabase> {
    const res = await getClient().put(`${BASE}/databases/${id}`, input, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async archiveDatabase(
    id: string,
    groupId: string,
    archived = true,
  ): Promise<ExportDatabase> {
    const res = await getClient().put(
      `${BASE}/databases/${id}/archive`,
      { archived: archived },
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  static async deleteDatabase(
    id: string,
    groupId: string,
  ): Promise<ExportDatabase> {
    const res = await getClient().delete(`${BASE}/databases/${id}`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  // ---- Products ----

  static async getProducts(
    databaseId: string,
    groupId: string,
    params: ListProductsParams = {},
  ): Promise<ProductsPage> {
    const res = await getClient().get(
      `${BASE}/databases/${databaseId}/products`,
      { params: { groupId: groupId, ...params } },
    );
    return {
      products: res.data?.data ?? [],
      pagination: res.data?.pagination ?? {
        currentPage: 1,
        totalItems: 0,
        itemsPerPage: params.limit ?? 25,
        totalPages: 1,
      },
    };
  }

  static async getProduct(id: string, groupId: string): Promise<ExportProduct> {
    const res = await getClient().get(`${BASE}/products/${id}`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async createProduct(
    databaseId: string,
    input: CreateExportProductInput,
  ): Promise<ExportProduct> {
    const res = await getClient().post(
      `${BASE}/databases/${databaseId}/products`,
      input,
    );
    return res.data?.data;
  }

  static async updateProduct(
    id: string,
    groupId: string,
    input: UpdateExportProductInput,
  ): Promise<ExportProduct> {
    const res = await getClient().put(`${BASE}/products/${id}`, input, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async deleteProduct(
    id: string,
    groupId: string,
  ): Promise<ExportProduct> {
    const res = await getClient().delete(`${BASE}/products/${id}`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async getProductFacets(
    databaseId: string,
    groupId: string,
  ): Promise<ProductFacetOptions> {
    const res = await getClient().get(
      `${BASE}/databases/${databaseId}/product-facets`,
      { params: { groupId: groupId } },
    );
    return (
      res.data?.data ?? {
        origins: [],
        destinations: [],
        htsChapters: [],
        total: 0,
      }
    );
  }

  // ---- Saved Views ----

  static async getSavedViews(
    groupId: string,
    scope: string,
  ): Promise<SavedView[]> {
    const res = await getClient().get(`${BASE}/saved-views`, {
      params: { groupId: groupId, scope: scope },
    });
    return res.data?.data ?? [];
  }

  static async createSavedView(
    input: CreateSavedViewInput,
  ): Promise<SavedView> {
    const res = await getClient().post(`${BASE}/saved-views`, input);
    return res.data?.data;
  }

  static async updateSavedView(
    id: string,
    groupId: string,
    input: UpdateSavedViewInput,
  ): Promise<SavedView> {
    const res = await getClient().put(`${BASE}/saved-views/${id}`, input, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async deleteSavedView(
    id: string,
    groupId: string,
  ): Promise<SavedView> {
    const res = await getClient().delete(`${BASE}/saved-views/${id}`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  // ---- Screenings ----

  static async getScreenings(
    productId: string,
    groupId: string,
  ): Promise<ScreeningResult[]> {
    const res = await getClient().get(`${BASE}/products/${productId}/screenings`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? [];
  }

  static async runScreening(
    productId: string,
    input: RunScreeningInput,
  ): Promise<ScreeningResult> {
    const res = await getClient().post(
      `${BASE}/products/${productId}/screenings`,
      input,
    );
    return res.data?.data;
  }

  // ---- Licenses ----

  static async getLicenses(
    databaseId: string,
    groupId: string,
    params: ListLicensesParams = {},
  ): Promise<ExportLicense[]> {
    const res = await getClient().get(`${BASE}/databases/${databaseId}/licenses`, {
      params: { groupId: groupId, ...params },
    });
    return res.data?.data ?? [];
  }

  static async getLicense(id: string, groupId: string): Promise<ExportLicense> {
    const res = await getClient().get(`${BASE}/licenses/${id}`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async requestLicense(
    input: CreateLicenseInput,
  ): Promise<ExportLicense> {
    const res = await getClient().post(`${BASE}/licenses`, input);
    return res.data?.data;
  }

  static async updateLicense(
    id: string,
    groupId: string,
    input: UpdateLicenseInput,
  ): Promise<ExportLicense> {
    const res = await getClient().put(`${BASE}/licenses/${id}`, input, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async deleteLicense(
    id: string,
    groupId: string,
  ): Promise<ExportLicense> {
    const res = await getClient().delete(`${BASE}/licenses/${id}`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  // ---- Checklist templates ----

  static async getChecklistTemplates(
    groupId: string,
  ): Promise<ChecklistTemplate[]> {
    const res = await getClient().get(`${BASE}/checklist-templates`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? [];
  }

  static async createChecklistTemplate(
    input: CreateChecklistTemplateInput,
  ): Promise<ChecklistTemplate> {
    const res = await getClient().post(`${BASE}/checklist-templates`, input);
    return res.data?.data;
  }

  static async updateChecklistTemplate(
    id: string,
    groupId: string,
    input: UpdateChecklistTemplateInput,
  ): Promise<ChecklistTemplate> {
    const res = await getClient().put(`${BASE}/checklist-templates/${id}`, input, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async deleteChecklistTemplate(
    id: string,
    groupId: string,
  ): Promise<ChecklistTemplate> {
    const res = await getClient().delete(`${BASE}/checklist-templates/${id}`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  // ---- Linking ----

  static async getLinkedLicenses(
    productId: string,
    groupId: string,
  ): Promise<ExportLicense[]> {
    const res = await getClient().get(`${BASE}/products/${productId}/licenses`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? [];
  }

  static async linkLicenses(
    productId: string,
    input: LinkLicensesInput,
  ): Promise<ExportLicense[]> {
    const res = await getClient().post(
      `${BASE}/products/${productId}/licenses`,
      input,
    );
    return res.data?.data ?? [];
  }

  static async unlinkLicense(
    productId: string,
    licenseId: string,
    groupId: string,
  ): Promise<void> {
    await getClient().delete(`${BASE}/products/${productId}/licenses/${licenseId}`, {
      params: { groupId: groupId },
    });
  }

  static async getLinkedProducts(
    licenseId: string,
    groupId: string,
  ): Promise<ExportProduct[]> {
    const res = await getClient().get(`${BASE}/licenses/${licenseId}/products`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? [];
  }

  static async linkProducts(
    licenseId: string,
    input: LinkProductsInput,
  ): Promise<ExportProduct[]> {
    const res = await getClient().post(
      `${BASE}/licenses/${licenseId}/products`,
      input,
    );
    return res.data?.data ?? [];
  }

  static async unlinkProductFromLicense(
    licenseId: string,
    productId: string,
    groupId: string,
  ): Promise<void> {
    await getClient().delete(`${BASE}/licenses/${licenseId}/products/${productId}`, {
      params: { groupId: groupId },
    });
  }

  /**
   * Set the planning ceilings for a product on a license. These are budgets —
   * what the product actually drew down comes from the drawdown ledger. Omit a
   * field to leave it unchanged; pass null to clear it.
   */
  static async setProductAllocation(
    licenseId: string,
    productId: string,
    groupId: string,
    // Absent = leave alone, null = clear, value = set — for every field,
    // including allocated_value. Never resend a value you didn't just edit.
    allocation: {
      allocated_value?: number;
      allocated_quantity?: number | null;
      default_unit_value?: number | null;
      usml_category?: string | null;
      commodity_code?: string | null;
    },
  ): Promise<ExportProduct[]> {
    const res = await getClient().put(
      `${BASE}/licenses/${licenseId}/products/${productId}/allocation`,
      allocation,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  // ----- License drawdown ledger --------------------------------------------

  static async getDrawdowns(
    licenseId: string,
    groupId: string,
  ): Promise<LicenseDrawdown[]> {
    const res = await getClient().get(
      `${BASE}/licenses/${licenseId}/drawdowns`,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  static async getProductDrawdowns(
    productId: string,
    groupId: string,
  ): Promise<LicenseDrawdown[]> {
    const res = await getClient().get(
      `${BASE}/products/${productId}/drawdowns`,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  static async getDrawdownSummary(
    licenseId: string,
    groupId: string,
  ): Promise<DrawdownSummary | null> {
    const res = await getClient().get(
      `${BASE}/licenses/${licenseId}/drawdown-summary`,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? null;
  }

  /** Returns the license's full ledger so callers can re-render in one pass. */
  static async createDrawdown(
    licenseId: string,
    input: CreateDrawdownInput,
  ): Promise<LicenseDrawdown[]> {
    const res = await getClient().post(
      `${BASE}/licenses/${licenseId}/drawdowns`,
      input,
    );
    return res.data?.data ?? [];
  }

  static async updateDrawdown(
    id: string,
    groupId: string,
    input: UpdateDrawdownInput,
  ): Promise<LicenseDrawdown | null> {
    const res = await getClient().put(`${BASE}/drawdowns/${id}`, input, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? null;
  }

  /** Voids a posted row (retained, struck through); deletes an unposted draft. */
  static async deleteDrawdown(
    id: string,
    groupId: string,
  ): Promise<{ voided: boolean }> {
    const res = await getClient().delete(`${BASE}/drawdowns/${id}`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? { voided: false };
  }

  static async getLicenseDocuments(
    licenseId: string,
    groupId: string,
  ): Promise<LicenseDocument[]> {
    const res = await getClient().get(
      `${BASE}/licenses/${licenseId}/documents`,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  static async uploadLicenseDocument(
    licenseId: string,
    groupId: string,
    file: File,
  ): Promise<LicenseDocument[]> {
    const client = getClient();
    // Strip the instance's JSON default so the browser sets multipart/form-data
    // (with the boundary). initAxios bakes Content-Type onto defaults.headers,
    // so all three locations must be cleared (matches BiteDBAPI's upload).
    delete client.defaults.headers.common['Content-Type'];
    delete (client.defaults.headers.post as Record<string, unknown>)[
      'Content-Type'
    ];
    delete (client.defaults.headers as Record<string, unknown>)['Content-Type'];
    const fd = new FormData();
    fd.append('file', file);
    // No explicit Content-Type — axios sets multipart/form-data with the
    // boundary from the FormData body.
    const res = await client.post(
      `${BASE}/licenses/${licenseId}/documents`,
      fd,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  static async downloadLicenseDocument(
    docId: string,
    groupId: string,
    fileName: string,
  ): Promise<void> {
    const res = await getClient().get(
      `${BASE}/licenses/documents/${docId}/download`,
      { params: { groupId: groupId }, responseType: 'blob' },
    );
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  static async deleteLicenseDocument(
    docId: string,
    groupId: string,
  ): Promise<void> {
    await getClient().delete(`${BASE}/licenses/documents/${docId}`, {
      params: { groupId: groupId },
    });
  }

  // ----- Product documents (S3-backed) + comments ---------------------------

  static async getProductDocuments(
    productId: string,
    groupId: string,
  ): Promise<ProductDocument[]> {
    const res = await getClient().get(
      `${BASE}/products/${productId}/documents`,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  static async uploadProductDocument(
    productId: string,
    groupId: string,
    file: File,
  ): Promise<ProductDocument[]> {
    const client = getClient();
    // Strip the instance's JSON default so the browser sets multipart/form-data
    // with the boundary (matches uploadLicenseDocument).
    delete client.defaults.headers.common['Content-Type'];
    delete (client.defaults.headers.post as Record<string, unknown>)[
      'Content-Type'
    ];
    delete (client.defaults.headers as Record<string, unknown>)['Content-Type'];
    const fd = new FormData();
    fd.append('file', file);
    const res = await client.post(
      `${BASE}/products/${productId}/documents`,
      fd,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  static async downloadProductDocument(
    docId: string,
    groupId: string,
    fileName: string,
  ): Promise<void> {
    const res = await getClient().get(
      `${BASE}/products/documents/${docId}/download`,
      { params: { groupId: groupId }, responseType: 'blob' },
    );
    const url = URL.createObjectURL(res.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  static async deleteProductDocument(
    docId: string,
    groupId: string,
  ): Promise<void> {
    await getClient().delete(`${BASE}/products/documents/${docId}`, {
      params: { groupId: groupId },
    });
  }

  static async getProductComments(
    productId: string,
    groupId: string,
  ): Promise<ProductComment[]> {
    const res = await getClient().get(
      `${BASE}/products/${productId}/comments`,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  static async addProductComment(
    productId: string,
    groupId: string,
    body: string,
  ): Promise<ProductComment> {
    const res = await getClient().post(
      `${BASE}/products/${productId}/comments`,
      { body: body },
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  static async deleteProductComment(
    productId: string,
    commentId: string,
    groupId: string,
  ): Promise<void> {
    await getClient().delete(
      `${BASE}/products/${productId}/comments/${commentId}`,
      { params: { groupId: groupId } },
    );
  }

  static async getProductActivity(
    productId: string,
    groupId: string,
  ): Promise<ExportActivity[]> {
    const res = await getClient().get(`${BASE}/products/${productId}/activity`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? [];
  }

  static async getLicenseActivity(
    licenseId: string,
    groupId: string,
  ): Promise<ExportActivity[]> {
    const res = await getClient().get(`${BASE}/licenses/${licenseId}/activity`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? [];
  }

  // ---- Bulk ----

  static async bulkApply(
    databaseId: string,
    input: BulkApplyInput,
  ): Promise<BulkApplyResult> {
    const res = await getClient().post(
      `${BASE}/databases/${databaseId}/products/bulk`,
      input,
    );
    return res.data?.data;
  }

  // ---- Bulk product import (screening plan Step 1) ----

  // Upload a CSV/XLSX of products into a database. Returns the import job with
  // inserted/failed tallies + a capped sample of row errors. Multipart, so the
  // JSON Content-Type default is stripped (matches uploadLicenseDocument).
  static async importProducts(
    databaseId: string,
    groupId: string,
    file: File,
  ): Promise<ExportImportJob> {
    const client = getClient();
    delete client.defaults.headers.common['Content-Type'];
    delete (client.defaults.headers.post as Record<string, unknown>)[
      'Content-Type'
    ];
    delete (client.defaults.headers as Record<string, unknown>)['Content-Type'];
    const fd = new FormData();
    fd.append('file', file);
    const res = await client.post(
      `${BASE}/databases/${databaseId}/products/import`,
      fd,
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  /**
   * Bulk-import shipments as drawdowns. Team-scoped, not database-scoped: rows
   * name their own license, and a shipment register routinely spans databases.
   */
  static async importShipments(
    groupId: string,
    file: File,
  ): Promise<ImportShipmentsResult> {
    const client = getClient();
    // Strip the JSON default so the browser sets multipart/form-data with its
    // boundary (matches importProducts / uploadLicenseDocument).
    delete client.defaults.headers.common['Content-Type'];
    delete (client.defaults.headers.post as Record<string, unknown>)[
      'Content-Type'
    ];
    delete (client.defaults.headers as Record<string, unknown>)['Content-Type'];
    const fd = new FormData();
    fd.append('file', file);
    const res = await client.post(`${BASE}/shipments/import`, fd, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async getImportJob(
    jobId: string,
    groupId: string,
  ): Promise<ExportImportJob | null> {
    const res = await getClient().get(`${BASE}/import-jobs/${jobId}`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? null;
  }

  static async listImportJobs(
    databaseId: string,
    groupId: string,
  ): Promise<ExportImportJob[]> {
    const res = await getClient().get(
      `${BASE}/databases/${databaseId}/import-jobs`,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  // ---- Bulk classification runs (screening plan Step 4) ----

  static async startClassification(
    databaseId: string,
    groupId: string,
  ): Promise<ExportScreeningRun> {
    const res = await getClient().post(
      `${BASE}/databases/${databaseId}/classify`,
      {},
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  // Bulk product export risk (commodity) screen over the database's products.
  static async startProductScreening(
    databaseId: string,
    groupId: string,
  ): Promise<ExportScreeningRun> {
    const res = await getClient().post(
      `${BASE}/databases/${databaseId}/screen-products`,
      {},
      { params: { groupId: groupId } },
    );
    return res.data?.data;
  }

  static async getScreeningRun(
    runId: string,
    groupId: string,
  ): Promise<ExportScreeningRun | null> {
    const res = await getClient().get(`${BASE}/screening-runs/${runId}`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? null;
  }

  // ---- Database screening settings ----
  static async getDatabaseSettings(
    databaseId: string,
    groupId: string,
  ): Promise<ExportDatabaseSettings> {
    const res = await getClient().get(`${BASE}/databases/${databaseId}/settings`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async updateDatabaseSettings(
    databaseId: string,
    input: UpdateDatabaseSettingsInput,
  ): Promise<ExportDatabaseSettings> {
    const res = await getClient().put(
      `${BASE}/databases/${databaseId}/settings`,
      input,
      { params: { groupId: input.group_id } },
    );
    return res.data?.data;
  }

  static async listScreeningRuns(
    databaseId: string,
    groupId: string,
  ): Promise<ExportScreeningRun[]> {
    const res = await getClient().get(
      `${BASE}/databases/${databaseId}/screening-runs`,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  static async getClassificationResults(
    productId: string,
    groupId: string,
  ): Promise<ExportClassificationResult[]> {
    const res = await getClient().get(
      `${BASE}/products/${productId}/classification`,
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  // Classify a single product on demand (product-detail per-check Run).
  static async classifyProduct(
    productId: string,
    groupId: string,
  ): Promise<ExportClassificationResult[]> {
    const res = await getClient().post(
      `${BASE}/products/${productId}/classify`,
      {},
      { params: { groupId: groupId } },
    );
    return res.data?.data ?? [];
  }

  // Ingest a completed Export Workflow's results into the Hub (WS1). Binds to an
  // existing product (id/SKU) or creates one, then writes ai-sourced results.
  static async ingestWorkflow(
    input: IngestWorkflowInput,
  ): Promise<IngestWorkflowResult> {
    const res = await getClient().post(
      `${BASE}/products/ingest-workflow`,
      input,
      { params: { groupId: input.group_id } },
    );
    return res.data?.data;
  }

  // ---- Hierarchy (BOM) ----

  static async getProductStructure(
    productId: string,
    groupId: string,
  ): Promise<ProductStructure> {
    const res = await getClient().get(`${BASE}/products/${productId}/structure`, {
      params: { groupId: groupId },
    });
    return (
      res.data?.data ?? { children: [], parents: [], child_issue_count: 0 }
    );
  }

  static async getBomEdges(
    databaseId: string,
    groupId: string,
  ): Promise<ProductBomEdge[]> {
    const res = await getClient().get(`${BASE}/databases/${databaseId}/bom-edges`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? [];
  }

  static async addBomLink(input: AddBomLinkInput): Promise<void> {
    await getClient().post(`${BASE}/bom`, input);
  }

  static async removeBomLink(
    parentId: string,
    childId: string,
    groupId: string,
  ): Promise<void> {
    await getClient().delete(`${BASE}/products/${parentId}/children/${childId}`, {
      params: { groupId: groupId },
    });
  }

  // ---- Entities (spec §4.7) ----

  static async getEntities(
    groupId: string,
    search?: string,
  ): Promise<ExportEntity[]> {
    const res = await getClient().get(`${BASE}/entities`, {
      params: { groupId: groupId, search: search || undefined },
    });
    return res.data?.data ?? [];
  }

  static async createEntity(input: CreateEntityInput): Promise<ExportEntity> {
    const res = await getClient().post(`${BASE}/entities`, input);
    return res.data?.data;
  }

  static async getFieldOptions(
    groupId: string,
    field: FieldOptionField,
  ): Promise<string[]> {
    const res = await getClient().get(`${BASE}/field-options`, {
      params: { groupId: groupId, field: field },
    });
    return res.data?.data ?? [];
  }

  static async addFieldOption(input: {
    group_id: string;
    field: FieldOptionField;
    value: string;
  }): Promise<string[]> {
    const res = await getClient().post(`${BASE}/field-options`, input);
    return res.data?.data ?? [];
  }

  static async getLinkedEntities(
    productId: string,
    groupId: string,
  ): Promise<ProductEntityLink[]> {
    const res = await getClient().get(`${BASE}/products/${productId}/entities`, {
      params: { groupId: groupId },
    });
    return res.data?.data ?? [];
  }

  static async linkEntity(
    productId: string,
    input: LinkEntityInput,
  ): Promise<ProductEntityLink[]> {
    const res = await getClient().post(
      `${BASE}/products/${productId}/entities`,
      input,
    );
    return res.data?.data ?? [];
  }

  static async unlinkEntity(
    productId: string,
    entityId: string,
    groupId: string,
  ): Promise<void> {
    await getClient().delete(`${BASE}/products/${productId}/entities/${entityId}`, {
      params: { groupId: groupId },
    });
  }

  // ---- Dashboards + Level 1 overviews (spec §5.2/§5.3/§9) ----

  static async getDatabaseDashboard(
    databaseId: string,
    groupId: string,
  ): Promise<DatabaseDashboard> {
    const res = await getClient().get(`${BASE}/databases/${databaseId}/dashboard`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async getDatabaseHistory(
    databaseId: string,
    groupId: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<DatabaseHistoryPage> {
    const res = await getClient().get(`${BASE}/databases/${databaseId}/history`, {
      params: { groupId: groupId, ...params },
    });
    return res.data?.data ?? { entries: [], total: 0 };
  }

  static async getDatabaseScreeningResults(
    databaseId: string,
    groupId: string,
    limit?: number,
  ): Promise<DatabaseScreeningResult[]> {
    const res = await getClient().get(
      `${BASE}/databases/${databaseId}/screening-results`,
      { params: { groupId: groupId, limit: limit } },
    );
    return res.data?.data ?? [];
  }

  static async getOverviewProducts(
    groupId: string,
    params: ListProductsParams = {},
  ): Promise<OverviewProductsPage> {
    const res = await getClient().get(`${BASE}/overview/products`, {
      params: { groupId: groupId, ...params },
    });
    return {
      products: res.data?.data ?? [],
      pagination: res.data?.pagination ?? {
        currentPage: 1,
        totalItems: 0,
        itemsPerPage: params.limit ?? 25,
        totalPages: 1,
      },
    };
  }

  static async getOverviewFacets(
    groupId: string,
  ): Promise<OverviewFacetOptions> {
    const res = await getClient().get(`${BASE}/overview/product-facets`, {
      params: { groupId: groupId },
    });
    return (
      res.data?.data ?? {
        origins: [],
        destinations: [],
        htsChapters: [],
        databases: [],
        total: 0,
      }
    );
  }

  static async getOverviewKpis(groupId: string): Promise<OverviewKpis> {
    const res = await getClient().get(`${BASE}/overview/kpis`, {
      params: { groupId: groupId },
    });
    return (
      res.data?.data ?? {
        total: 0,
        compliant: 0,
        action_required: 0,
        restricted: 0,
        unscreened: 0,
        recent_screenings_7d: 0,
      }
    );
  }

  static async getLicenseOverview(groupId: string): Promise<LicenseOverview> {
    const res = await getClient().get(`${BASE}/overview/licenses`, {
      params: { groupId: groupId },
    });
    return res.data?.data;
  }

  static async getNotifications(
    groupId: string,
    limit = 60,
  ): Promise<WorkspaceFeedItem[]> {
    const res = await getClient().get(`${BASE}/notifications`, {
      params: { groupId: groupId, limit: limit },
    });
    return res.data?.data ?? [];
  }
}
